import { AddressService, UspsAddress } from '@/@types/generated'
import {
  AddressValidationDialog,
  AddressValidationDialogProps,
} from '@/components/Molecules/AddressValidationDialog'
import { ZipCodeRegex } from '@/data/states'
import { useCallback, useRef, useState } from 'react'

type AddressWithType = AddressValidationDialogProps['enteredAddress']

type DialogState = {
  enteredAddress: AddressWithType
  suggestedAddress?: UspsAddress
}

export function useAddressValidationDialog() {
  const [dialogState, setDialogState] = useState<DialogState | null>(null)
  const resolve = useRef<
    ((value: AddressWithType | null, isVerified: boolean) => void) | null
  >(null)

  const closeDialog = useCallback(
    (result: AddressWithType | null, isVerified: boolean) => {
      setDialogState(null)
      if (!resolve.current) return
      resolve.current(result, isVerified)
      resolve.current = null
    },
    []
  )

  const openDialog = useCallback(
    (enteredAddress: AddressWithType, suggestedAddress?: UspsAddress) => {
      setDialogState({ enteredAddress, suggestedAddress })
      return new Promise<AddressWithType | null>((r) => {
        resolve.current = r
      })
    },
    []
  )

  const validateAddress = useCallback(async (address?: AddressWithType) => {
    if (!address) return { isValid: true, suggestedAddress: undefined }

    // Not a US zip, skip USPS validation.
    if (!ZipCodeRegex.test(address.postalCode ?? ''))
      return { isValid: true, suggestedAddress: undefined }

    try {
      return await AddressService.verifyUspsAddress({
        streetAddress: address.street1,
        city: address.city,
        state: address.stateProvince,
        zipCode: address.postalCode,
      })
    } catch {
      return { isValid: false, suggestedAddress: undefined }
    }
  }, [])

  const validateAndConfirm = useCallback(
    async (
      address?: AddressWithType
    ): Promise<{
      address: AddressWithType | null
      isVerified: boolean
    }> => {
      if (!address) return { address: null, isVerified: false }

      const { isValid, suggestedAddress } = await validateAddress(address)

      // Valid — apply USPS formatting silently, no dialog needed
      if (isValid) {
        const formatted: AddressWithType = {
          ...address,
          street1: suggestedAddress?.streetAddress || address.street1,
          street2: address.street2, // street2 is never passed to USPS and therefore never returned
          city: suggestedAddress?.city || address.city,
          stateProvince: suggestedAddress?.state || address.stateProvince,
          postalCode: suggestedAddress?.zipCode
            ? `${suggestedAddress?.zipCode}${suggestedAddress?.zipPlus4 ? `-${suggestedAddress?.zipPlus4}` : ''}`
            : address.postalCode,
        }
        return { address: formatted, isVerified: true }
      }

      return {
        address: await openDialog(address, suggestedAddress),
        isVerified: false,
      }
    },
    [validateAddress, openDialog]
  )

  const dialog = dialogState ? (
    <AddressValidationDialog
      enteredAddress={dialogState.enteredAddress}
      suggestedAddress={dialogState.suggestedAddress}
      open
      onOpenChange={(open) => {
        if (!open) closeDialog(null, false)
      }}
      onBack={() => closeDialog(null, false)}
      onUseSuggested={() => {
        const { suggestedAddress, enteredAddress } = dialogState
        if (!suggestedAddress) return
        closeDialog(
          {
            ...enteredAddress,
            street1: suggestedAddress.streetAddress || enteredAddress.street1,
            street2: enteredAddress.street2, // street2 is never passed to USPS and therefore never returned
            city: suggestedAddress.city || enteredAddress.city,
            stateProvince:
              suggestedAddress.state || enteredAddress.stateProvince,
            postalCode: suggestedAddress.zipCode
              ? `${suggestedAddress.zipCode}${suggestedAddress.zipPlus4 ? `-${suggestedAddress.zipPlus4}` : ''}`
              : enteredAddress.postalCode,
          },
          true
        )
      }}
      onContinueAnyway={() => closeDialog(dialogState.enteredAddress, false)}
    />
  ) : null

  return { validateAndConfirm, dialog }
}
